import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import CheckInTool from '../components/CheckInTool';
import CheckInToolMobile from '../components/CheckInToolMobile';

import { values } from '../CheckIn/checkInValues';
import styles from './SecondaryCheckInModal.module.scss';

const REASON_OPTIONS = [
  'Family',
  'Friends',
  'Teacher',
  'Classmates',
  'Homework/Exam',
  'Work',
  'Health',
  'Energy Level',
  'Not Sure',
];

const DARK_TEXT_COLORS = new Set(['#daeff1', '#fef8d8', '#f5c4b1']);

const getContrastColor = (hexColor: string): string => {
  return DARK_TEXT_COLORS.has(hexColor.toLowerCase()) ? '#43484d' : '#fff';
};

interface SecondaryCheckInModalProps {
  studentName: string;
  onEmotionSelect: (emotion: string, reasons: string[]) => void;
  onClose: () => void;
  isSubmitting: boolean;
}

const SecondaryCheckInModal: React.FC<SecondaryCheckInModalProps> = ({
  studentName,
  onEmotionSelect,
  onClose,
  isSubmitting,
}) => {
  const [selectedEmotion, setSelectedEmotion] = useState<string>('');
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [showReasons, setShowReasons] = useState(false);
  const isSmall = useMediaQuery({ query: '(max-width: 820px)' });

  const selectedColor = selectedEmotion
    ? values.find((v) => v.subEmotions.includes(selectedEmotion))?.subEmotionColor
    : undefined;

  const handleEmotionClick = (emotion: string) => {
    if (!isSubmitting && emotion && emotion.trim() !== '') {
      setSelectedEmotion(emotion);
      setShowReasons(true);
    }
  };

  const handleReasonClick = (reason: string) => {
    setSelectedReasons((prev) => {
      if (prev.includes(reason)) return prev.filter((r) => r !== reason);
      if (prev.length >= 2) return prev;
      return [...prev, reason];
    });
  };

  const handleSubmit = () => {
    if (!selectedEmotion) {
      return;
    }
    onEmotionSelect(selectedEmotion, selectedReasons);
  };

  const handleGoBack = () => {
    setShowReasons(false);
    setSelectedEmotion('');
    setSelectedReasons([]);
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={isSmall ? styles.modalContentMobile : styles.modalContentDesktop}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerText}>
            {showReasons ? (
              <>
                <h2>
                  What's causing you to feel{' '}
                  <span
                    className={styles.headerEmotionBadge}
                    style={{
                      backgroundColor:
                        values.find((v) => v.subEmotions.includes(selectedEmotion))?.subEmotionColor || '#e0e0e0',
                      color: getContrastColor(
                        values.find((v) => v.subEmotions.includes(selectedEmotion))?.subEmotionColor || '#e0e0e0',
                      ),
                    }}
                  >
                    {selectedEmotion}
                  </span>
                  ?
                </h2>
              </>
            ) : (
              <>
                <h2>How are you feeling right now?</h2>
                <p>Hi {studentName}, select an emotion from the wheel to check in</p>
              </>
            )}
          </div>
          <button onClick={onClose} disabled={isSubmitting} className={styles.closeButton}>
            <svg fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
        </div>

        {/* Emotion Wheel - shown when not showing reasons */}
        {!showReasons && (
          <>
            <div className={styles.wheelContainer}>
              {isSmall ? (
                <CheckInToolMobile
                  handleEmotionClick={handleEmotionClick}
                  selectedEmotion={selectedEmotion}
                  selectedColor={selectedColor}
                />
              ) : (
                <CheckInTool
                  handleEmotionClick={handleEmotionClick}
                  selectedEmotion={selectedEmotion}
                  selectedColor={selectedColor}
                />
              )}
            </div>
            <div className={styles.footer}>
              <p>
                {isSmall
                  ? 'First tap an inner category, then select a specific emotion from the outer ring'
                  : 'Click on any emotion in the outer ring to check in'}
              </p>
              <div className={styles.footerDisclaimer}>
                Disclaimer: The check-in tool is not used for assessment purposes
              </div>
            </div>
          </>
        )}

        {/* Emotion Sources/Reasons - shown after selecting emotion */}
        {showReasons && (
          <>
            <div className={styles.reasonGrid}>
              {REASON_OPTIONS.map((reason) => (
                <button
                  key={reason}
                  className={`${styles.reasonButton} ${selectedReasons.includes(reason) ? styles.reasonButtonSelected : ''}`}
                  onClick={() => handleReasonClick(reason)}
                  disabled={isSubmitting}
                >
                  {reason}
                </button>
              ))}
            </div>
            <div className={styles.buttonRow}>
              <button
                className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
                onClick={handleGoBack}
                disabled={isSubmitting}
              >
                Go Back
              </button>
              <button
                className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                Done
              </button>
            </div>
            <div className={styles.disclaimer}>Disclaimer: The check-in tool is not used for assessment purposes</div>
          </>
        )}

        {/* Loading overlay */}
        {isSubmitting && (
          <div className={styles.submittingOverlay}>
            <div className={styles.submittingContent}>
              <div className={styles.spinner}></div>
              <p>Saving check-in...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecondaryCheckInModal;
